import type { Signature } from "../helpers/signature-api";
import {
  getAllCampaigns,
  getCampaign,
  getSignatureCampaignMappings,
  type Campaign,
  type SignatureCampaignMappingItem,
} from "../helpers/campaign-api";
import {
  estimateCombinedSlotLength,
  isOverSlotLimit,
  SET_SIGNATURE_ASYNC_MAX_CHARS,
  SIGNATURE_WARN_CHARS,
} from "../utils/signatureCharLimits";

export type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const FILTER_ICON_FUNNEL = `<svg class="sunnysign-mapping-filter__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>`;

const FILTER_ICON_CHEVRON = `<svg class="sunnysign-mapping-filter__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`;

const COLLAPSE_ICON = `<svg class="sunnysign-mapping-box__collapse-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="7 13 12 18 17 13"/><polyline points="7 6 12 11 17 6"/></svg>`;

const FILTER_ICON_LIST = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>`;

const FILTER_ICON_CHECK = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;

const FILTER_ICON_SQUARE = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>`;

type MappingFilterKind = "signatures" | "campaigns";
type SelectionFilter = "all" | "selected" | "unselected";
type StatusFilter = "all" | "active" | "inactive" | "planned";

function buildMappingFilterDropdownHtml(kind: MappingFilterKind, t: TranslateFn): string {
  const statusSection =
    kind === "campaigns"
      ? `
      <div class="sunnysign-mapping-filter__divider" role="separator"></div>
      <p class="sunnysign-mapping-filter__section">${escapeHtml(t("campaigns.mappingFilterStatus"))}</p>
      <button type="button" class="sunnysign-mapping-filter__item" data-status-filter="all" role="menuitemradio" aria-checked="true">
        <span class="sunnysign-mapping-filter__dot sunnysign-mapping-filter__dot--all"></span>
        <span>${escapeHtml(t("campaigns.mappingFilterAllStatus"))}</span>
        <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
      </button>
      <button type="button" class="sunnysign-mapping-filter__item" data-status-filter="active" role="menuitemradio" aria-checked="false">
        <span class="sunnysign-mapping-filter__dot sunnysign-mapping-filter__dot--active"></span>
        <span>${escapeHtml(t("campaigns.statusActive"))}</span>
        <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
      </button>
      <button type="button" class="sunnysign-mapping-filter__item" data-status-filter="inactive" role="menuitemradio" aria-checked="false">
        <span class="sunnysign-mapping-filter__dot sunnysign-mapping-filter__dot--inactive"></span>
        <span>${escapeHtml(t("campaigns.statusInactive"))}</span>
        <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
      </button>
      <button type="button" class="sunnysign-mapping-filter__item" data-status-filter="planned" role="menuitemradio" aria-checked="false">
        <span class="sunnysign-mapping-filter__dot sunnysign-mapping-filter__dot--planned"></span>
        <span>${escapeHtml(t("campaigns.statusPlanned"))}</span>
        <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
      </button>`
      : "";

  return `
    <div class="sunnysign-mapping-filter" data-kind="${kind}">
      <button type="button" class="sunnysign-mapping-filter__trigger" aria-haspopup="true" aria-expanded="false">
        ${FILTER_ICON_FUNNEL}
        <span class="sunnysign-mapping-filter__label">${escapeHtml(t("campaigns.mappingFilter"))}</span>
        ${FILTER_ICON_CHEVRON}
      </button>
      <div class="sunnysign-mapping-filter__menu" role="menu" hidden>
        <p class="sunnysign-mapping-filter__section">${escapeHtml(t("campaigns.mappingFilterSelection"))}</p>
        <button type="button" class="sunnysign-mapping-filter__item" data-filter="all" role="menuitemradio" aria-checked="true">
          <span class="sunnysign-mapping-filter__item-icon">${FILTER_ICON_LIST}</span>
          <span>${escapeHtml(t("campaigns.mappingFilterAll"))}</span>
          <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
        </button>
        <button type="button" class="sunnysign-mapping-filter__item" data-filter="selected" role="menuitemradio" aria-checked="false">
          <span class="sunnysign-mapping-filter__item-icon">${FILTER_ICON_CHECK}</span>
          <span>${escapeHtml(t("campaigns.mappingFilterSelectedOnly"))}</span>
          <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
        </button>
        <button type="button" class="sunnysign-mapping-filter__item" data-filter="unselected" role="menuitemradio" aria-checked="false">
          <span class="sunnysign-mapping-filter__item-icon">${FILTER_ICON_SQUARE}</span>
          <span>${escapeHtml(t("campaigns.mappingFilterUnselectedOnly"))}</span>
          <span class="sunnysign-mapping-filter__check" aria-hidden="true">✓</span>
        </button>
        ${statusSection}
      </div>
    </div>`;
}

function buildMappingControlsHtml(kind: MappingFilterKind, t: TranslateFn): string {
  return `
    <div class="sunnysign-mapping-box__controls">
      <label class="sunnysign-mapping-box__select-all">
        <input type="checkbox" class="sunnysign-mapping-box__select-all-check" />
        <span>${escapeHtml(t("campaigns.mappingSelectAllVisible"))}</span>
      </label>
      ${buildMappingFilterDropdownHtml(kind, t)}
    </div>`;
}

function closeAllMappingFilterMenus(): void {
  document.querySelectorAll(".sunnysign-mapping-filter__menu").forEach((menu) => {
    menu.setAttribute("hidden", "");
  });
  document.querySelectorAll(".sunnysign-mapping-filter__trigger").forEach((trigger) => {
    trigger.setAttribute("aria-expanded", "false");
  });
}

let mappingFilterOutsideCloseBound = false;

function ensureMappingFilterOutsideClose(): void {
  if (mappingFilterOutsideCloseBound) return;
  mappingFilterOutsideCloseBound = true;
  document.addEventListener("click", () => closeAllMappingFilterMenus());
}

function updateMappingFilterMenu(
  root: HTMLElement,
  selectionFilter: SelectionFilter,
  statusFilter: StatusFilter
): void {
  root.querySelectorAll(".sunnysign-mapping-filter__item[data-filter]").forEach((btn) => {
    const value = (btn as HTMLElement).getAttribute("data-filter") as SelectionFilter;
    const active = value === selectionFilter;
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-checked", active ? "true" : "false");
  });
  root.querySelectorAll(".sunnysign-mapping-filter__item[data-status-filter]").forEach((btn) => {
    const value = (btn as HTMLElement).getAttribute("data-status-filter") as StatusFilter;
    const active = value === statusFilter;
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-checked", active ? "true" : "false");
  });
}

function wireMappingFilterDropdown(
  root: HTMLElement,
  state: { filter: SelectionFilter; statusFilter?: StatusFilter },
  refresh: () => void
): void {
  const filterWrap = root.querySelector(".sunnysign-mapping-filter");
  if (!filterWrap) return;

  const trigger = filterWrap.querySelector(".sunnysign-mapping-filter__trigger") as HTMLButtonElement | null;
  const menu = filterWrap.querySelector(".sunnysign-mapping-filter__menu") as HTMLElement | null;
  if (!trigger || !menu) return;

  trigger.addEventListener("click", (e) => {
    e.stopPropagation();
    const willOpen = menu.hasAttribute("hidden");
    closeAllMappingFilterMenus();
    if (willOpen) {
      menu.removeAttribute("hidden");
      trigger.setAttribute("aria-expanded", "true");
    }
  });

  menu.querySelectorAll("[data-filter]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      state.filter = (btn as HTMLElement).getAttribute("data-filter") as SelectionFilter;
      closeAllMappingFilterMenus();
      refresh();
    });
  });

  menu.querySelectorAll("[data-status-filter]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (state.statusFilter !== undefined) {
        state.statusFilter = (btn as HTMLElement).getAttribute("data-status-filter") as StatusFilter;
      }
      closeAllMappingFilterMenus();
      refresh();
    });
  });

  ensureMappingFilterOutsideClose();
}

function wireMappingBoxCollapse(box: HTMLElement): void {
  const trigger = box.querySelector(".sunnysign-mapping-box__collapse-trigger") as HTMLButtonElement | null;
  const body = box.querySelector(".sunnysign-mapping-box__body") as HTMLElement | null;
  if (!trigger || !body || trigger.dataset.bound) return;
  trigger.dataset.bound = "1";

  trigger.addEventListener("click", () => {
    const collapsed = box.classList.toggle("sunnysign-mapping-box--collapsed");
    body.toggleAttribute("hidden", collapsed);
    trigger.setAttribute("aria-expanded", collapsed ? "false" : "true");
  });
}

function wireSelectAllCheckbox(
  box: HTMLElement,
  getVisibleIds: () => number[],
  state: { selectedIds: Set<number> },
  onChange: () => void,
  refresh: () => void
): void {
  const input = box.querySelector(".sunnysign-mapping-box__select-all-check") as HTMLInputElement | null;
  if (!input || input.dataset.bound) return;
  input.dataset.bound = "1";

  input.addEventListener("change", () => {
    const visibleIds = getVisibleIds();
    if (input.checked) visibleIds.forEach((id) => state.selectedIds.add(id));
    else visibleIds.forEach((id) => state.selectedIds.delete(id));
    onChange();
    refresh();
  });
}

function updateSelectAllCheckbox(box: HTMLElement, visibleIds: number[], state: { selectedIds: Set<number> }): void {
  const input = box.querySelector(".sunnysign-mapping-box__select-all-check") as HTMLInputElement | null;
  if (!input) return;

  if (visibleIds.length === 0) {
    input.checked = false;
    input.indeterminate = false;
    input.disabled = true;
    return;
  }

  input.disabled = false;
  const selectedVisible = visibleIds.filter((id) => state.selectedIds.has(id)).length;
  input.checked = selectedVisible === visibleIds.length;
  input.indeterminate = selectedVisible > 0 && selectedVisible < visibleIds.length;
}

function formatDateRange(start?: string | null, end?: string | null, locale = "de-DE"): string {
  const fmt = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(locale);
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

function statusLabel(t: TranslateFn, status: string): string {
  if (status === "active") return t("campaigns.statusActive");
  if (status === "planned") return t("campaigns.statusPlanned");
  return t("campaigns.statusInactive");
}

function statusClass(status: string): string {
  if (status === "active") return "sunnysign-campaign-status--active";
  if (status === "planned") return "sunnysign-campaign-status--planned";
  return "sunnysign-campaign-status--inactive";
}

function computeCampaignStatus(campaign: { startsAtUtc?: string | null; endsAtUtc?: string | null }): string {
  const now = new Date();
  if (campaign.startsAtUtc && new Date(campaign.startsAtUtc) > now) return "planned";
  if (campaign.endsAtUtc && new Date(campaign.endsAtUtc) < now) return "inactive";
  return "active";
}

function toCampaignMappingItem(campaign: Campaign): SignatureCampaignMappingItem {
  return {
    campaignId: campaign.id,
    name: campaign.name,
    startsAtUtc: campaign.startsAtUtc,
    endsAtUtc: campaign.endsAtUtc,
    status: computeCampaignStatus(campaign),
    assigned: false,
  };
}

export type SignaturePickerState = {
  selectedIds: Set<number>;
  filter: SelectionFilter;
  search: string;
};

export function createSignaturePickerState(selectedIds: number[]): SignaturePickerState {
  return {
    selectedIds: new Set(selectedIds),
    filter: "all",
    search: "",
  };
}

export function getSelectedSignatureIds(state: SignaturePickerState): number[] {
  return Array.from(state.selectedIds);
}

function filterSignatures(
  signatures: Signature[],
  state: SignaturePickerState
): { filtered: Signature[]; selectedCount: number; total: number } {
  const search = state.search.trim().toLowerCase();
  const filtered = signatures.filter((s) => {
    const matchesSearch =
      !search || s.name.toLowerCase().includes(search) || (s.content ?? "").toLowerCase().includes(search);
    const selected = state.selectedIds.has(s.id);
    const matchesTab =
      state.filter === "all" ||
      (state.filter === "selected" && selected) ||
      (state.filter === "unselected" && !selected);
    return matchesSearch && matchesTab;
  });
  return { filtered, selectedCount: state.selectedIds.size, total: signatures.length };
}

function buildSignatureListHtml(filtered: Signature[], state: SignaturePickerState, t: TranslateFn): string {
  if (filtered.length === 0) {
    return `<p class="sunnysign-mapping-box__empty">${escapeHtml(t("campaigns.mappingEmpty"))}</p>`;
  }
  return filtered
    .map((sig) => {
      const preview = (sig.content ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
      const checked = state.selectedIds.has(sig.id) ? "checked" : "";
      return `
            <label class="sunnysign-mapping-row">
              <input type="checkbox" class="sunnysign-mapping-row__check" data-signature-id="${sig.id}" ${checked} />
              <span class="sunnysign-mapping-row__body">
                <span class="sunnysign-mapping-row__name">${escapeHtml(sig.name)}</span>
                <span class="sunnysign-mapping-row__meta">${escapeHtml(preview)}</span>
              </span>
            </label>`;
    })
    .join("");
}

function bindSignatureListChecks(
  listEl: HTMLElement,
  state: SignaturePickerState,
  onChange: () => void,
  refresh: () => void
): void {
  listEl.querySelectorAll(".sunnysign-mapping-row__check").forEach((input) => {
    input.addEventListener("change", (e) => {
      const id = parseInt((e.target as HTMLInputElement).getAttribute("data-signature-id") || "0", 10);
      if ((e.target as HTMLInputElement).checked) state.selectedIds.add(id);
      else state.selectedIds.delete(id);
      onChange();
      refresh();
    });
  });
}

function updateSignatureMappingBox(
  box: HTMLElement,
  signatures: Signature[],
  state: SignaturePickerState,
  t: TranslateFn,
  onChange: () => void
): void {
  const { filtered, selectedCount, total } = filterSignatures(signatures, state);

  const titleEl = box.querySelector(".sunnysign-mapping-box__title");
  if (titleEl) titleEl.textContent = t("campaigns.mappingSignaturesTitle", { count: selectedCount });

  const filterRoot = box.querySelector(".sunnysign-mapping-box__controls") as HTMLElement | null;
  if (filterRoot) updateMappingFilterMenu(filterRoot, state.filter, "all");

  const visibleIds = filtered.map((s) => s.id);
  updateSelectAllCheckbox(box, visibleIds, state);

  const listEl = box.querySelector(".sunnysign-mapping-box__list") as HTMLElement | null;
  if (listEl) {
    listEl.innerHTML = buildSignatureListHtml(filtered, state, t);
    bindSignatureListChecks(listEl, state, onChange, () =>
      updateSignatureMappingBox(box, signatures, state, t, onChange)
    );
  }

  const countEl = box.querySelector(".sunnysign-mapping-box__count");
  if (countEl) countEl.textContent = t("campaigns.mappingSelectedCount", { selected: selectedCount, total });
}

function mountSignatureMappingBox(container: HTMLElement, t: TranslateFn): HTMLElement {
  container.innerHTML = `
    <div class="sunnysign-mapping-box sunnysign-mapping-box--collapsed">
      <button type="button" class="sunnysign-mapping-box__collapse-trigger" aria-expanded="false">
        <h3 class="sunnysign-mapping-box__title"></h3>
        <span class="sunnysign-mapping-box__collapse-icon">${COLLAPSE_ICON}</span>
      </button>
      <div class="sunnysign-mapping-box__body" hidden>
        <p class="sunnysign-mapping-box__hint">${escapeHtml(t("campaigns.mappingSignaturesHint"))}</p>
        <input type="search" class="sunnysign-input sunnysign-mapping-box__search" id="campaign-sig-search" placeholder="${escapeHtml(t("campaigns.mappingSearchSignatures"))}" autocomplete="off" />
        ${buildMappingControlsHtml("signatures", t)}
        <div class="sunnysign-mapping-box__list"></div>
        <div class="sunnysign-mapping-box__footer">
          <span class="sunnysign-mapping-box__count"></span>
        </div>
        <p class="sunnysign-mapping-box__length" id="campaign-form-mapping-length" hidden></p>
        <p class="sunnysign-mapping-box__info">${escapeHtml(t("campaigns.mappingSignaturesInfo"))}</p>
      </div>
    </div>`;
  return container.querySelector(".sunnysign-mapping-box")!;
}

export function renderSignatureMappingBox(
  container: HTMLElement,
  signatures: Signature[],
  state: SignaturePickerState,
  t: TranslateFn,
  onChange: () => void
): void {
  let box = container.querySelector(".sunnysign-mapping-box") as HTMLElement | null;
  if (!box) {
    box = mountSignatureMappingBox(container, t);
    wireMappingBoxCollapse(box);
    const searchInput = box.querySelector("#campaign-sig-search") as HTMLInputElement;
    const refresh = () => updateSignatureMappingBox(box!, signatures, state, t, onChange);
    const getVisibleIds = () => filterSignatures(signatures, state).filtered.map((s) => s.id);

    searchInput.addEventListener("input", (e) => {
      state.search = (e.target as HTMLInputElement).value;
      refresh();
    });

    wireMappingFilterDropdown(box, state, refresh);
    wireSelectAllCheckbox(box, getVisibleIds, state, onChange, refresh);
  } else {
    const searchInput = box.querySelector("#campaign-sig-search") as HTMLInputElement | null;
    if (searchInput && document.activeElement !== searchInput) {
      searchInput.value = state.search;
    }
  }

  updateSignatureMappingBox(box, signatures, state, t, onChange);
}

export type CampaignPickerState = {
  selectedIds: Set<number>;
  filter: SelectionFilter;
  statusFilter: StatusFilter;
  search: string;
};

let signatureFormCampaignPickerState: CampaignPickerState | null = null;
/** campaignId → HTML content (for combined slot length estimates). */
const signatureFormCampaignContentById = new Map<number, string>();
let signatureFormMappingOptions: {
  getSignatureHtml?: () => string;
  onValidationChange?: (ok: boolean) => void;
} | null = null;

export function getSignatureFormSelectedCampaignIds(): number[] {
  return signatureFormCampaignPickerState ? Array.from(signatureFormCampaignPickerState.selectedIds) : [];
}

export function createCampaignPickerState(selectedIds: number[]): CampaignPickerState {
  return {
    selectedIds: new Set(selectedIds),
    filter: "all",
    statusFilter: "all",
    search: "",
  };
}

function filterCampaigns(
  campaigns: SignatureCampaignMappingItem[],
  state: CampaignPickerState
): { filtered: SignatureCampaignMappingItem[]; selectedCount: number; total: number } {
  const search = state.search.trim().toLowerCase();
  const filtered = campaigns.filter((c) => {
    const matchesSearch = !search || c.name.toLowerCase().includes(search);
    const selected = state.selectedIds.has(c.campaignId);
    const matchesSelection =
      state.filter === "all" ||
      (state.filter === "selected" && selected) ||
      (state.filter === "unselected" && !selected);
    const matchesStatus = state.statusFilter === "all" || c.status === state.statusFilter;
    return matchesSearch && matchesSelection && matchesStatus;
  });
  return { filtered, selectedCount: state.selectedIds.size, total: campaigns.length };
}

function buildCampaignListHtml(
  filtered: SignatureCampaignMappingItem[],
  state: CampaignPickerState,
  t: TranslateFn
): string {
  if (filtered.length === 0) {
    return `<p class="sunnysign-mapping-box__empty">${escapeHtml(t("campaigns.mappingEmptyCampaigns"))}</p>`;
  }
  return filtered
    .map((c) => {
      const checked = state.selectedIds.has(c.campaignId) ? "checked" : "";
      return `
      <label class="sunnysign-mapping-row sunnysign-mapping-row--campaign">
        <input type="checkbox" class="sunnysign-mapping-row__check" data-campaign-id="${c.campaignId}" ${checked} />
        <span class="sunnysign-mapping-row__body">
          <span class="sunnysign-mapping-row__title-line">
            <span class="sunnysign-mapping-row__name">${escapeHtml(c.name)}</span>
            <span class="sunnysign-campaign-status ${statusClass(c.status)}">${escapeHtml(statusLabel(t, c.status))}</span>
          </span>
          <span class="sunnysign-mapping-row__meta">${escapeHtml(formatDateRange(c.startsAtUtc, c.endsAtUtc))}</span>
        </span>
      </label>`;
    })
    .join("");
}

function bindCampaignListChecks(
  listEl: HTMLElement,
  state: CampaignPickerState,
  onChange: () => void,
  refresh: () => void
): void {
  listEl.querySelectorAll(".sunnysign-mapping-row__check").forEach((input) => {
    input.addEventListener("change", (e) => {
      const id = parseInt((e.target as HTMLInputElement).getAttribute("data-campaign-id") || "0", 10);
      if ((e.target as HTMLInputElement).checked) state.selectedIds.add(id);
      else state.selectedIds.delete(id);
      onChange();
      refresh();
    });
  });
}

function updateCampaignMappingBox(
  box: HTMLElement,
  campaigns: SignatureCampaignMappingItem[],
  state: CampaignPickerState,
  t: TranslateFn,
  onChange: () => void
): void {
  const { filtered, selectedCount, total } = filterCampaigns(campaigns, state);

  const titleEl = box.querySelector(".sunnysign-mapping-box__title");
  if (titleEl) titleEl.textContent = t("campaigns.mappingCampaignsTitle", { count: selectedCount });

  const filterRoot = box.querySelector(".sunnysign-mapping-box__controls") as HTMLElement | null;
  if (filterRoot) updateMappingFilterMenu(filterRoot, state.filter, state.statusFilter);

  const visibleIds = filtered.map((c) => c.campaignId);
  updateSelectAllCheckbox(box, visibleIds, state);

  const listEl = box.querySelector(".sunnysign-mapping-box__list") as HTMLElement | null;
  if (listEl) {
    listEl.innerHTML = buildCampaignListHtml(filtered, state, t);
    bindCampaignListChecks(listEl, state, onChange, () =>
      updateCampaignMappingBox(box, campaigns, state, t, onChange)
    );
  }

  const countEl = box.querySelector(".sunnysign-mapping-box__count");
  if (countEl) countEl.textContent = t("campaigns.mappingSelectedCountCampaigns", { selected: selectedCount, total });
}

function mountCampaignMappingBox(container: HTMLElement, t: TranslateFn): HTMLElement {
  container.innerHTML = `
    <div class="sunnysign-mapping-box sunnysign-mapping-box--collapsed">
      <button type="button" class="sunnysign-mapping-box__collapse-trigger" aria-expanded="false">
        <h3 class="sunnysign-mapping-box__title"></h3>
        <span class="sunnysign-mapping-box__collapse-icon">${COLLAPSE_ICON}</span>
      </button>
      <div class="sunnysign-mapping-box__body" hidden>
        <p class="sunnysign-mapping-box__hint">${escapeHtml(t("campaigns.signatureDialogHint"))}</p>
        <input type="search" class="sunnysign-input sunnysign-mapping-box__search" id="sig-form-campaign-search" placeholder="${escapeHtml(t("campaigns.mappingSearchCampaigns"))}" autocomplete="off" />
        ${buildMappingControlsHtml("campaigns", t)}
        <div class="sunnysign-mapping-box__list"></div>
        <div class="sunnysign-mapping-box__footer">
          <span class="sunnysign-mapping-box__count"></span>
        </div>
        <p class="sunnysign-mapping-box__length" id="sig-form-mapping-length" hidden></p>
        <p class="sunnysign-mapping-box__info">${escapeHtml(t("campaigns.mappingCampaignsInfo"))}</p>
      </div>
    </div>`;
  return container.querySelector(".sunnysign-mapping-box")!;
}

export function renderCampaignMappingBox(
  container: HTMLElement,
  campaigns: SignatureCampaignMappingItem[],
  state: CampaignPickerState,
  t: TranslateFn,
  onChange: () => void
): void {
  let box = container.querySelector(".sunnysign-mapping-box") as HTMLElement | null;
  if (!box) {
    box = mountCampaignMappingBox(container, t);
    wireMappingBoxCollapse(box);
    const searchInput = box.querySelector("#sig-form-campaign-search") as HTMLInputElement;
    const refresh = () => updateCampaignMappingBox(box!, campaigns, state, t, onChange);
    const getVisibleIds = () => filterCampaigns(campaigns, state).filtered.map((c) => c.campaignId);

    searchInput.addEventListener("input", (e) => {
      state.search = (e.target as HTMLInputElement).value;
      refresh();
    });

    wireMappingFilterDropdown(box, state, refresh);
    wireSelectAllCheckbox(box, getVisibleIds, state, onChange, refresh);
  } else {
    const searchInput = box.querySelector("#sig-form-campaign-search") as HTMLInputElement | null;
    if (searchInput && document.activeElement !== searchInput) {
      searchInput.value = state.search;
    }
  }

  updateCampaignMappingBox(box, campaigns, state, t, onChange);
}

export async function initSignatureFormCampaignMapping(
  container: HTMLElement,
  signatureId: number | null,
  t: TranslateFn,
  options?: {
    getSignatureHtml?: () => string;
    onValidationChange?: (ok: boolean) => void;
  }
): Promise<void> {
  container.innerHTML = "";
  signatureFormCampaignPickerState = null;
  signatureFormCampaignContentById.clear();

  let campaigns: SignatureCampaignMappingItem[];
  let selectedIds: number[];

  if (signatureId) {
    const data = await getSignatureCampaignMappings(signatureId);
    campaigns = data.campaigns ?? [];
    selectedIds = campaigns.filter((c) => c.assigned).map((c) => c.campaignId);
  } else {
    const all = await getAllCampaigns();
    campaigns = all.map(toCampaignMappingItem);
    selectedIds = all.map((c) => c.id);
  }

  // Prefetch HTML for combined length estimates (list DTOs omit content).
  await Promise.all(
    campaigns.map(async (c) => {
      try {
        const detail = await getCampaign(c.campaignId);
        signatureFormCampaignContentById.set(c.campaignId, detail.content ?? "");
      } catch {
        signatureFormCampaignContentById.set(c.campaignId, "");
      }
    })
  );

  signatureFormCampaignPickerState = createCampaignPickerState(selectedIds);
  signatureFormMappingOptions = options ?? null;

  renderCampaignMappingBox(container, campaigns, signatureFormCampaignPickerState, t, () => {
    signatureFormMappingOptions?.onValidationChange?.(true);
  });
  refreshSignatureFormMappingLength(t);
}

export function refreshSignatureFormMappingLength(t: TranslateFn): boolean {
  const hint = document.getElementById("sig-form-mapping-length");
  const getHtml = signatureFormMappingOptions?.getSignatureHtml;
  if (!signatureFormCampaignPickerState || !getHtml) {
    if (hint) {
      hint.hidden = true;
      hint.textContent = "";
    }
    return true;
  }

  const status = getSignatureFormMappingLengthStatus(getHtml());
  if (!hint) {
    return !status.overSlot;
  }

  if (status.overSlot) {
    hint.hidden = false;
    hint.className = "sunnysign-mapping-box__length sunnysign-mapping-box__length--error";
    hint.textContent = t("campaigns.mappingTooLong", {
      len: String(status.estimated),
      max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
    });
  } else if (status.inWarn) {
    hint.hidden = false;
    hint.className = "sunnysign-mapping-box__length sunnysign-mapping-box__length--warn";
    hint.textContent = t("campaigns.mappingLengthWarn", {
      len: String(status.estimated),
      max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
    });
  } else {
    hint.hidden = true;
    hint.textContent = "";
  }
  return !status.overSlot;
}

export function getSignatureFormMappingLengthStatus(signatureHtml: string): {
  estimated: number;
  overSlot: boolean;
  inWarn: boolean;
} {
  const ids = getSignatureFormSelectedCampaignIds();
  const campaignHtmls = ids.map((id) => signatureFormCampaignContentById.get(id) ?? "");
  const estimated = estimateCombinedSlotLength(signatureHtml, campaignHtmls);
  return {
    estimated,
    overSlot: isOverSlotLimit(estimated),
    inWarn: estimated >= SIGNATURE_WARN_CHARS && !isOverSlotLimit(estimated),
  };
}

/** Campaign form: max estimated length across selected signatures + campaign HTML. */
export function getCampaignFormMappingLengthStatus(
  campaignHtml: string,
  signatures: Signature[],
  selectedSignatureIds: number[]
): { estimated: number; overSlot: boolean; inWarn: boolean; worstSignatureId: number | null } {
  let worst = 0;
  let worstId: number | null = null;
  for (const id of selectedSignatureIds) {
    const sig = signatures.find((s) => s.id === id);
    const estimated = estimateCombinedSlotLength(sig?.content ?? "", [campaignHtml]);
    if (estimated > worst) {
      worst = estimated;
      worstId = id;
    }
  }
  if (selectedSignatureIds.length === 0) {
    worst = estimateCombinedSlotLength("", [campaignHtml]);
  }
  return {
    estimated: worst,
    overSlot: isOverSlotLimit(worst),
    inWarn: worst >= SIGNATURE_WARN_CHARS && !isOverSlotLimit(worst),
    worstSignatureId: worstId,
  };
}

export function updateCampaignFormMappingLengthHint(
  t: TranslateFn,
  campaignHtml: string,
  signatures: Signature[],
  selectedSignatureIds: number[]
): boolean {
  const hint = document.getElementById("campaign-form-mapping-length");
  const status = getCampaignFormMappingLengthStatus(campaignHtml, signatures, selectedSignatureIds);
  if (!hint) return !status.overSlot;

  if (status.overSlot) {
    hint.hidden = false;
    hint.className = "sunnysign-mapping-box__length sunnysign-mapping-box__length--error";
    hint.textContent = t("campaigns.mappingTooLong", {
      len: String(status.estimated),
      max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
    });
  } else if (status.inWarn) {
    hint.hidden = false;
    hint.className = "sunnysign-mapping-box__length sunnysign-mapping-box__length--warn";
    hint.textContent = t("campaigns.mappingLengthWarn", {
      len: String(status.estimated),
      max: String(SET_SIGNATURE_ASYNC_MAX_CHARS),
    });
  } else {
    hint.hidden = true;
    hint.textContent = "";
  }
  return !status.overSlot;
}
